import {
  Component,
  createEffect,
  createSignal,
  For,
  JSXElement,
  mergeProps,
  Show,
  splitProps,
  untrack
} from 'solid-js';
import * as Tabs from './Component.tsx';
import './tab.utils.scss';
import { twMerge } from 'tailwind-merge';

export type TabListType = {
  id: string;
  label: JSXElement;
  disabled?: boolean;
  content: Component;
  hidden?: boolean;
  class?: string;
  dataTestId?: string;
};

type TabWrapperProps = {
  list: TabListType[];
  invalidator?: string;
};

type TabProps = Tabs.RootProps & TabWrapperProps;

export const TabWrapper = (props: TabProps) => {
  const merged = mergeProps({}, props);
  const [wrapperProps, arkProps] = splitProps(merged, ['list', 'invalidator']);

  const getTabContentMap = () => {
    return wrapperProps.list.reduce((acc, item) => {
      acc[item.id] = item.content;
      return acc;
    }, {});
  };

  const [tabContentMap, setTabContentMap] =
    createSignal<Record<string, Component>>(getTabContentMap());

  createEffect(() => {
    if (merged.invalidator) {
      untrack(() => {
        setTabContentMap(getTabContentMap());
      });
    }
  });

  return (
    <Tabs.Root {...arkProps}>
      <Tabs.List>
        <For each={wrapperProps.list}>
          {(item) => (
            <Show when={!item.hidden}>
              <Tabs.Trigger
                value={item.id}
                disabled={!!item.disabled}
                class={`px-3`}
                data-testid={item?.dataTestId ?? item.id}
              >
                {item.label}
              </Tabs.Trigger>
            </Show>
          )}
        </For>
        <Tabs.Indicator />
      </Tabs.List>
      <For each={wrapperProps.list}>
        {(item) => {
          return (
            <Tabs.Content
              value={item.id}
              class={twMerge(item.class ?? ``)}
            >
              {(() => {
                // Consumed as a component instead of JSXElement
                // to initialise lazily
                // otherwise the component would be called once
                // during the initialization of the list prop of this component
                // This also allows lazy bundling and mounting of the content component
                const ContentComponent = tabContentMap()[item.id];
                return <ContentComponent />;
              })()}
            </Tabs.Content>
          );
        }}
      </For>
    </Tabs.Root>
  );
};
